/**
 * Requastor performing legacy "Glassd Web Service"-style authentication.
 *
 * @since 0.1.0
 */
import { createHash, createHmac } from 'crypto';

import { match } from 'fp-ts/Either';
import { Decoder } from 'io-ts';

import { Requestor } from '~/src/api/Requestor';
import { TokenSecret } from '~/src/model/Authentication';
import { Method, xhr } from '~/src/api/xhr';
import { jsonPromise } from '~/src/model/Util';


/**
 * Create a `Requestor` for legacy "Glassd Web Service"-style authentication,
 * probably derived from AWS authenication.
 *
 * @since 0.1.0
 */
export const CreateRequestor = (endpoint: string, auth: TokenSecret): Requestor => {
	const authorization = (url: URL, method: Method, body: string): string => {
		const message = method + url.pathname + url.search + createHash('md5').update(body).digest('hex');
		return 'GWS ' + auth.token + ':' + createHmac('sha1', auth.secret).update(message).digest('hex');
	};

	const requestRaw = (path: string, method: Method, data?: object|undefined): Promise<object> => {
		const body = data === undefined || method === 'GET' ? undefined : JSON.stringify(data);
		const url = new URL(endpoint + '/' + path);

		return xhr(method, url.toString(), {
			Authorization: authorization(url, method, body || ''),
			'Content-Type': 'application/json;charset=utf-8',
		}, body).then(jsonPromise);
	};

	const request = <T>(dec: Decoder<unknown,T>) => (path: string, method: Method, data?: object|undefined): Promise<T> =>
		requestRaw(path, method, data).then((resp) => match(
			(failures) => Promise.reject({ error: 'JSON decode failure', response: resp, failures }),
			(good: T) => Promise.resolve(good)
		)(dec.decode(resp))).catch((e) => Promise.reject({ error: 'Request error', response: e }));

	return {
		requestRaw,
		request,
	};
};
